import type { Dispatch } from "react";
import type { EditorAction, RegionEditorTab } from "../editor/reducers";
import type { RegionContextMenuContentFragment } from "../client/generated";
import { useTranslation } from "react-i18next";
import { useDeleteRegionFromContextMutation } from "../client/generated";
import { getTableFromRegion } from "../tables/utils";
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@acdc2/ui/components/context-menu";

type Props = {
  fragment: RegionContextMenuContentFragment;
  dispatch: Dispatch<EditorAction>;
};

export function RegionContextMenuContent({ fragment, dispatch }: Props) {
  const { t } = useTranslation();
  const [deleteRegion] = useDeleteRegionFromContextMutation();

  const onDelete = async () => {
    await deleteRegion({
      variables: { regionId: fragment.id },
      optimisticResponse: {
        deleteRegion: {
          userErrors: [],
          region: {
            __typename: "Region",
            id: fragment.id,
          },
        },
      },
      update(cache) {
        const id = cache.identify(fragment);
        cache.evict({ id });
        cache.gc();
      },
    });
  };

  const onEdit = () => {
    dispatch({
      type: "switchToRegionTab",
      tab: "update",
      regionId: fragment.id,
    });
  };

  const switchTo = (tab: RegionEditorTab) => {
    dispatch({
      type: "switchToRegionTab",
      tab,
      regionId: fragment.id,
    });
  };

  return (
    <ContextMenuContent
      className="w-48"
      onMouseDown={(event) => event.stopPropagation()}
      onCloseAutoFocus={(event) => event.preventDefault()}
    >
      <ContextMenuItem onClick={onEdit}>
        {t("RegionContextMenuContent.labels.edit")}
      </ContextMenuItem>
      <ContextMenuSub>
        <ContextMenuSubTrigger>
          {t("RegionContextMenuContent.labels.convert")}
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className="w-48">
          <ContextMenuItem onClick={() => switchTo("heading")}>
            {t("RegionContextMenuContent.labels.heading")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.heading")}
            </ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => switchTo("text")}>
            {t("RegionContextMenuContent.labels.text")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.text")}
            </ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => switchTo("formula")}>
            {t("RegionContextMenuContent.labels.formula")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.formula")}
            </ContextMenuShortcut>
          </ContextMenuItem>

          <ContextMenuItem
            onClick={() => {
              dispatch({
                type: "switchToRegionTab",
                tab: "table",
                regionId: fragment.id,
                table: getTableFromRegion(fragment),
              });
            }}
          >
            {t("RegionContextMenuContent.labels.table")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.table")}
            </ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => switchTo("image")}>
            {t("RegionContextMenuContent.labels.image")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.image")}
            </ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => switchTo("figure")}>
            {t("RegionContextMenuContent.labels.figure")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.figure")}
            </ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => switchTo("caption")}>
            {t("RegionContextMenuContent.labels.caption")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.caption")}
            </ContextMenuShortcut>
          </ContextMenuItem>

          <ContextMenuItem onClick={() => switchTo("list")}>
            {t("RegionContextMenuContent.labels.list")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.list")}
            </ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => switchTo("listItem")}>
            {t("RegionContextMenuContent.labels.listItem")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.listItem")}
            </ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => switchTo("sublist")}>
            {t("RegionContextMenuContent.labels.sublist")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.sublist")}
            </ContextMenuShortcut>
          </ContextMenuItem>

          <ContextMenuItem onClick={() => switchTo("artifact")}>
            {t("RegionContextMenuContent.labels.artifact")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.artifact")}
            </ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onClick={() => switchTo("toc")}>
            {t("RegionContextMenuContent.labels.toc")}
            <ContextMenuShortcut>
              {t("RegionContextMenuContent.shortcuts.toc")}
            </ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => switchTo("hybrid")}>
            {t("RegionContextMenuContent.labels.hybrid")}
          </ContextMenuItem>
        </ContextMenuSubContent>
      </ContextMenuSub>
      <ContextMenuItem onClick={onDelete}>
        {t("RegionContextMenuContent.labels.delete")}
        <ContextMenuShortcut>
          {t("RegionContextMenuContent.shortcuts.delete")}
        </ContextMenuShortcut>
      </ContextMenuItem>
    </ContextMenuContent>
  );
}
